import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { X, MagnifyingGlass, Plus } from "@phosphor-icons/react";

/*
  AttendeeSelector · Iter 85.65
  ──────────────────────────────
  Contact-aware attendee picker for Meeting Notes. Given the currently-
  selected client (and/or engagement), surfaces every canonical Contact
  associated with that Client so the advisor can multi-select from real
  records — no more freetext-only, no more comma-separated fragility.

  Contract:
    · value          — string[] of attendee display names (what gets
                       persisted on `note.attendees`). Legacy shape
                       preserved verbatim so historical notes read
                       identically.
    · onChange       — receives the mutated array.
    · clientId       — narrows the contact pool (client.stakeholders[]).
    · engagementId   — optional; today we treat every client contact as
                       fair game for any engagement of that client, since
                       stakeholders live on the client record. Kept in
                       the API so a future engagement-scoped model can
                       plug in without breaking callers.

  Behavior:
    · Selected attendees render as removable chips at the top.
    · Typeahead input reveals matching client contacts (name/title).
    · "Add '<typed name>'" affordance appears when the query has no
      exact match — the advisor gets an instant attendee row without
      being forced to create a Contact record first.
    · Optional inline "Create contact profile" affordance next to a
      manually-typed name pops the ContactsPanel deep-link into a new
      tab (no forced navigation while composing a note).
    · Never double-adds: existing selections (case-insensitive) are
      filtered out of both the picker list and the typed-name CTA.

  Data:
    · Fetches the client on mount to pull stakeholders[]. Falls back
      quietly to an empty pool if the client hasn't been chosen yet.
*/
export default function AttendeeSelector({ value = [], onChange, clientId, engagementId, testIdPrefix = "attendees" }) {
  const [pool, setPool] = useState([]); // client.stakeholders
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!clientId) { setPool([]); return; }
    let alive = true;
    api.get(`/clients/${clientId}`)
      .then((r) => { if (alive) setPool(r.data?.stakeholders || []); })
      .catch(() => { if (alive) setPool([]); });
    return () => { alive = false; };
  }, [clientId]);

  const selectedSet = useMemo(
    () => new Set((value || []).map((n) => (n || "").trim().toLowerCase())),
    [value]
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pool.filter((c) => {
      if (!c?.name) return false;
      if (selectedSet.has(c.name.trim().toLowerCase())) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        (c.title || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
      );
    }).slice(0, 8);
  }, [pool, query, selectedSet]);

  const addName = (name) => {
    const n = (name || "").trim();
    if (!n) return;
    if (selectedSet.has(n.toLowerCase())) return;
    onChange?.([...(value || []), n]);
    setQuery("");
  };
  const removeAt = (idx) => {
    const next = [...(value || [])];
    next.splice(idx, 1);
    onChange?.(next);
  };

  const typedExactHitInPool = pool.some((c) => (c?.name || "").trim().toLowerCase() === query.trim().toLowerCase());
  const typedAlreadySelected = selectedSet.has(query.trim().toLowerCase());
  const showTypedAddCta = query.trim().length > 0 && !typedExactHitInPool && !typedAlreadySelected;

  return (
    <div className="relative" data-testid={`${testIdPrefix}-selector`}>
      <div className="flex flex-wrap items-center gap-1.5 border border-border bg-secondary/40 px-2 py-1.5 min-h-[38px] focus-within:border-primary">
        {(value || []).map((name, i) => {
          const known = pool.find((c) => (c?.name || "").trim().toLowerCase() === (name || "").trim().toLowerCase());
          return (
            <span
              key={`${name}-${i}`}
              data-testid={`${testIdPrefix}-chip-${i}`}
              className={`inline-flex items-center gap-1 text-xs pl-2 pr-1 py-0.5 border ${
                known ? "border-primary/60 bg-primary/10 text-primary" : "border-border bg-background text-foreground"
              }`}
              title={known ? `${known.title || "Partner org contact"}${known.email ? ` · ${known.email}` : ""}` : "Free-text attendee"}
            >
              {name}
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Remove ${name}`}
                data-testid={`${testIdPrefix}-chip-remove-${i}`}
              >
                <X size={11} weight="bold" />
              </button>
            </span>
          );
        })}
        <div className="flex items-center gap-1 flex-1 min-w-[120px]">
          <MagnifyingGlass size={11} className="text-muted-foreground shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && query.trim()) {
                e.preventDefault();
                addName(query);
              }
              if (e.key === "Backspace" && !query && (value || []).length > 0) {
                removeAt((value || []).length - 1);
              }
            }}
            placeholder={
              (value || []).length === 0
                ? (clientId ? "Search or add attendees…" : "Attendees")
                : ""
            }
            className="flex-1 bg-transparent text-xs outline-none border-none px-1 py-0.5 min-w-[100px]"
            data-testid={`${testIdPrefix}-input`}
          />
        </div>
      </div>
      {focused && (matches.length > 0 || showTypedAddCta) && (
        <div
          className="absolute z-20 top-full left-0 right-0 mt-1 border border-border bg-card shadow-lg max-h-56 overflow-y-auto"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {matches.map((c) => (
            <button
              key={c.id || c.name}
              type="button"
              onClick={() => addName(c.name)}
              data-testid={`${testIdPrefix}-option-${c.id || c.name}`}
              className="w-full text-left px-3 py-2 text-xs hover:bg-secondary/60 flex items-center justify-between gap-2 border-b border-border/50 last:border-b-0"
            >
              <span className="min-w-0">
                <span className="font-medium">{c.name}</span>
                {c.title && <span className="text-muted-foreground"> · {c.title}</span>}
              </span>
              <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-primary shrink-0">Contact</span>
            </button>
          ))}
          {showTypedAddCta && (
            <button
              type="button"
              onClick={() => addName(query)}
              data-testid={`${testIdPrefix}-add-typed`}
              className="w-full text-left px-3 py-2 text-xs hover:bg-secondary/60 flex items-center gap-2 border-t border-border/50"
            >
              <Plus size={11} weight="bold" className="text-primary" />
              <span>Add “<span className="font-medium">{query.trim()}</span>” as attendee</span>
              {clientId && (
                <a
                  href={`/clients/${clientId}?tab=stakeholders&createContact=${encodeURIComponent(query.trim())}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="ml-auto text-[0.5625rem] font-mono uppercase tracking-widest text-brass hover:underline"
                  data-testid={`${testIdPrefix}-create-contact-link`}
                >
                  Create contact profile
                </a>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
